import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, AlertTriangle, CheckCircle2, Clock, FileEdit,
} from 'lucide-react';
import { type PropertyFeatures } from './PropertyFeaturesDialog';
import { WizardProgress, type WizardStep } from './add-property/WizardProgress';
import { StepShell } from './add-property/StepShell';
import { Step1Purpose } from './add-property/Step1Purpose';
import { Step2Details } from './add-property/Step2Details';
import { Step3Photos } from './add-property/Step3Photos';
import { Step4Features } from './add-property/Step4Features';
import { Step5Review } from './add-property/Step5Review';
import { QuotaUpsell } from './QuotaUpsell';

const WIZARD_STEPS: WizardStep[] = [
  { id: 1, label: 'Purpose & Location', shortLabel: 'Location' },
  { id: 2, label: 'Property Details', shortLabel: 'Details' },
  { id: 3, label: 'Photos', shortLabel: 'Photos' },
  { id: 4, label: 'Features', shortLabel: 'Features' },
  { id: 5, label: 'Contact & Review', shortLabel: 'Review' },
];

const statusBanner: Record<string, { label: string; color: string; icon: any; description: string }> = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700 border-gray-300', icon: FileEdit, description: 'Not yet submitted. Save as draft or submit for QC review.' },
  pending_review: { label: 'Pending QC Review', color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: Clock, description: 'Currently awaiting QC review. You can edit and resubmit.' },
  approved: { label: 'Approved & Live', color: 'bg-green-100 text-green-800 border-green-300', icon: CheckCircle2, description: 'This property is live. Submitting changes will queue them for QC review — the live version stays visible until your edits are approved.' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800 border-red-300', icon: AlertTriangle, description: 'QC rejected this property. Address the feedback and resubmit for review.' },
};

export function AddProperty() {
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: propertyId } = useParams<{ id: string }>();
  const isEditMode = !!propertyId;

  // Check if agent has agency membership
  const { data: agentProfile, isLoading: checkingAgency } = useQuery({
    queryKey: ['agent-agency-check', user?.id],
    queryFn: async () => {
      const { data } = await supabase.from('agent_profiles').select('agency_id').eq('user_id', user!.id).single();
      return data;
    },
    enabled: !!user && hasRole('agent'),
  });

  const isAgentWithoutAgency = hasRole('agent') && !hasRole('agency_admin') && !hasRole('superadmin') && !checkingAgency && !agentProfile?.agency_id;

  // Form state
  const [listingType, setListingType] = useState<'sale' | 'rent'>('sale');
  const [propertyType, setPropertyType] = useState('apartment');
  const [city, setCity] = useState('');
  const [subCity, setSubCity] = useState('');
  const [woreda, setWoreda] = useState('');
  const [address, setAddress] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');

  const [titleEn, setTitleEn] = useState('');
  const [titleAm, setTitleAm] = useState('');
  const [descEn, setDescEn] = useState('');
  const [descAm, setDescAm] = useState('');
  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState('ETB');
  const [areaSqm, setAreaSqm] = useState('');
  const [bedrooms, setBedrooms] = useState('');
  const [bathrooms, setBathrooms] = useState('');
  const [floor, setFloor] = useState('');
  const [totalFloors, setTotalFloors] = useState('');
  const [yearBuilt, setYearBuilt] = useState('');

  const [features, setFeatures] = useState<PropertyFeatures>({});

  const [contactPerson, setContactPerson] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [assignedAgentId, setAssignedAgentId] = useState<string | null>(null);

  const [images, setImages] = useState<{ url: string; is_primary: boolean }[]>([]);
  const [uploading, setUploading] = useState(false);
  const [loadedProperty, setLoadedProperty] = useState<any>(null);

  // Load existing property in edit mode
  const { data: existingProperty, isLoading: loadingExisting } = useQuery({
    queryKey: ['edit-property', propertyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('properties')
        .select('*, property_images(url, is_primary, sort_order)')
        .eq('id', propertyId!)
        .eq('user_id', user!.id)
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Property not found or you do not have permission to edit it.');
      return data;
    },
    enabled: isEditMode && !!user,
  });

  // Hydrate state from loaded property (preferring pending_changes when present)
  useEffect(() => {
    if (!existingProperty || loadedProperty?.id === existingProperty.id) return;
    const pc: any = existingProperty.has_pending_changes && existingProperty.pending_changes ? existingProperty.pending_changes : null;
    const get = (field: string, fallback: any = null) => (pc && pc[field] !== undefined ? pc[field] : (existingProperty as any)[field] ?? fallback);

    const titleObj = get('title', {});
    const descObj = get('description', {});
    setTitleEn(titleObj?.en || '');
    setTitleAm(titleObj?.am || '');
    setDescEn(descObj?.en || '');
    setDescAm(descObj?.am || '');
    setListingType(get('listing_type', 'sale'));
    setPropertyType(get('property_type', 'apartment'));
    setCity(get('city', '') || '');
    setSubCity(get('sub_city', '') || '');
    setWoreda(get('woreda', '') || '');
    setAddress(get('address', '') || '');
    setLatitude(get('latitude') != null ? String(get('latitude')) : '');
    setLongitude(get('longitude') != null ? String(get('longitude')) : '');
    setPrice(get('price') != null ? String(get('price')) : '');
    setCurrency(get('currency', 'ETB') || 'ETB');
    setAreaSqm(get('area_sqm') != null ? String(get('area_sqm')) : '');
    setBedrooms(get('bedrooms') != null ? String(get('bedrooms')) : '');
    setBathrooms(get('bathrooms') != null ? String(get('bathrooms')) : '');
    setFloor(get('floor') != null ? String(get('floor')) : '');
    setTotalFloors(get('total_floors') != null ? String(get('total_floors')) : '');
    setYearBuilt(get('year_built') != null ? String(get('year_built')) : '');
    setFeatures(get('features', {}) || {});
    setContactPerson(get('contact_person', '') || '');
    setContactPhone(get('contact_phone', '') || '');
    setContactEmail(get('contact_email', '') || '');
    setAssignedAgentId(get('assigned_agent_id', null));

    // Images
    if (pc && Array.isArray(pc._images)) {
      setImages(pc._images.map((i: any) => ({ url: i.url, is_primary: !!i.is_primary })));
    } else {
      const imgs = (existingProperty.property_images || []).slice().sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0));
      setImages(imgs.map((i: any) => ({ url: i.url, is_primary: !!i.is_primary })));
    }

    setLoadedProperty(existingProperty);
  }, [existingProperty, loadedProperty?.id]);

  // Pre-fill contact from profile (create mode only)
  useQuery({
    queryKey: ['my-profile-for-add-property'],
    queryFn: async () => {
      if (!user || isEditMode) return null;
      const { data } = await supabase.from('profiles').select('*').eq('user_id', user.id).single();
      if (data) {
        if (!contactPerson && data.full_name) setContactPerson(data.full_name);
        if (!contactPhone && data.phone) setContactPhone(data.phone);
        if (!contactEmail && user.email) setContactEmail(user.email);
      }
      return data;
    },
    enabled: !!user && !isEditMode,
  });

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !user) return;
    setUploading(true);
    try {
      const newImages: { url: string; is_primary: boolean }[] = [];
      for (const file of Array.from(e.target.files)) {
        const ext = file.name.split('.').pop();
        const path = `${user.id}/properties/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
        const { error } = await supabase.storage.from('property-images').upload(path, file);
        if (error) throw error;
        const { data: urlData } = supabase.storage.from('property-images').getPublicUrl(path);
        newImages.push({ url: urlData.publicUrl, is_primary: images.length === 0 && newImages.length === 0 });
      }
      setImages(prev => [...prev, ...newImages]);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (idx: number) => {
    setImages(prev => {
      const updated = prev.filter((_, i) => i !== idx);
      if (updated.length > 0 && !updated.some(img => img.is_primary)) {
        updated[0].is_primary = true;
      }
      return updated;
    });
  };

  const setPrimaryImage = (idx: number) => {
    setImages(prev => prev.map((img, i) => ({ ...img, is_primary: i === idx })));
  };

  const reorderImages = (from: number, to: number) => {
    setImages(prev => {
      const arr = [...prev];
      const [moved] = arr.splice(from, 1);
      arr.splice(to, 0, moved);
      return arr;
    });
  };

  const buildPayload = () => ({
    title: { en: titleEn, am: titleAm || titleEn, om: '' } as any,
    description: { en: descEn, am: descAm || descEn, om: '' } as any,
    property_type: propertyType as any,
    listing_type: listingType as any,
    price: parseFloat(price),
    currency: currency as any,
    city,
    sub_city: subCity || null,
    woreda: woreda || null,
    address: address || null,
    latitude: latitude ? Number(latitude) : null,
    longitude: longitude ? Number(longitude) : null,
    area_sqm: areaSqm ? parseFloat(areaSqm) : null,
    bedrooms: bedrooms ? parseInt(bedrooms) : null,
    bathrooms: bathrooms ? parseInt(bathrooms) : null,
    floor: floor ? parseInt(floor) : null,
    total_floors: totalFloors ? parseInt(totalFloors) : null,
    year_built: yearBuilt ? parseInt(yearBuilt) : null,
    features: features as any,
    contact_person: contactPerson || null,
    contact_phone: contactPhone || null,
    contact_email: contactEmail || null,
    assigned_agent_id: assignedAgentId,
  });

  const replaceImages = async (pid: string) => {
    await supabase.from('property_images').delete().eq('property_id', pid);
    if (images.length > 0) {
      const imgRows = images.map((img, i) => ({
        property_id: pid,
        url: img.url,
        is_primary: img.is_primary,
        sort_order: i,
      }));
      const { error: imgErr } = await supabase.from('property_images').insert(imgRows);
      if (imgErr) throw imgErr;
    }
  };

  // Notify QC team about a (re)submission
  const notifyQC = async (pid: string, propTitle: string, isResubmit: boolean, isApprovedEdit: boolean) => {
    const { data: lastReview } = await supabase
      .from('property_review_history' as any)
      .select('reviewer_id')
      .eq('property_id', pid)
      .in('action', ['feedback_sent', 'rejected', 'approved'])
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    const recipients = new Set<string>();
    if (lastReview && (lastReview as any).reviewer_id) recipients.add((lastReview as any).reviewer_id);

    const { data: qcLeads } = await supabase
      .from('user_roles')
      .select('user_id')
      .in('role', ['quality_control_lead', 'quality_control_agent']);
    (qcLeads || []).forEach((r: any) => recipients.add(r.user_id));

    if (recipients.size === 0) return;

    const title = isApprovedEdit
      ? `Property edits submitted: ${propTitle}`
      : isResubmit
        ? `Property resubmitted: ${propTitle}`
        : `New property submitted: ${propTitle}`;
    const message = isApprovedEdit
      ? 'A property owner submitted edits to a live listing for QC review.'
      : isResubmit
        ? 'The property owner addressed feedback and resubmitted this listing.'
        : 'A new property is awaiting QC review.';

    await supabase.from('notifications').insert(
      Array.from(recipients).map(uid => ({
        user_id: uid,
        type: isApprovedEdit ? 'property_edits_pending' : 'property_resubmitted',
        title,
        message,
        metadata: { property_id: pid, action: isApprovedEdit ? 'edits_submitted' : 'resubmitted' },
      }))
    );
  };

  const saveMutation = useMutation({
    mutationFn: async (submitForReview: boolean) => {
      if (!user) throw new Error('Not authenticated');
      if (!titleEn || !city || !price || !propertyType || !listingType) {
        throw new Error('Please fill all required fields (Title, City, Price, Property Type)');
      }

      if (!isEditMode) {
        // CREATE
        const slug = titleEn.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') + '-' + Date.now().toString(36);
        const { data: property, error } = await supabase
          .from('properties')
          .insert({
            ...buildPayload(),
            user_id: user.id,
            slug,
            status: submitForReview ? 'active' : 'draft',
            approval_status: submitForReview ? 'pending_review' : 'draft',
          } as any)
          .select()
          .single();
        if (error) throw error;

        if (images.length > 0) {
          const imgRows = images.map((img, i) => ({
            property_id: property.id,
            url: img.url,
            is_primary: img.is_primary,
            sort_order: i,
          }));
          const { error: imgErr } = await supabase.from('property_images').insert(imgRows);
          if (imgErr) throw imgErr;
        }

        if (submitForReview) {
          await supabase.from('property_review_history' as any).insert({
            property_id: property.id, reviewer_id: user.id, action: 'resubmitted', notes: 'Initial submission for review',
          });
          await notifyQC(property.id, titleEn, false, false);
        }
        return { property, submitForReview };
      }

      // EDIT
      const pid = propertyId!;
      const wasApproved = loadedProperty?.approval_status === 'approved';

      if (wasApproved && submitForReview) {
        // Option B: stash edits in pending_changes, keep live row intact
        const pendingPayload: any = {
          ...buildPayload(),
          _images: images,
        };
        const { error: updErr } = await supabase
          .from('properties')
          .update({
            pending_changes: pendingPayload,
            has_pending_changes: true,
            reviewer_notes: null,
          } as any)
          .eq('id', pid);
        if (updErr) throw updErr;

        await supabase.from('property_review_history' as any).insert({
          property_id: pid, reviewer_id: user.id, action: 'resubmitted', notes: 'Edited approved property — submitted for re-approval',
        });
        await notifyQC(pid, titleEn, true, true);
        return { property: { id: pid }, submitForReview, approvedEdit: true };
      }

      // Direct write for draft / pending / rejected
      const newApproval = submitForReview ? 'pending_review' : (loadedProperty?.approval_status || 'draft');
      const updatePayload: any = {
        ...buildPayload(),
        approval_status: newApproval,
      };
      if (submitForReview) updatePayload.reviewer_notes = null;

      const { error: updErr } = await supabase.from('properties').update(updatePayload).eq('id', pid);
      if (updErr) throw updErr;

      await replaceImages(pid);

      if (submitForReview) {
        await supabase.from('property_review_history' as any).insert({
          property_id: pid, reviewer_id: user.id, action: 'resubmitted', notes: 'Edited and resubmitted for review',
        });
        await notifyQC(pid, titleEn, true, false);
      }
      return { property: { id: pid }, submitForReview };
    },
    onSuccess: (result: any) => {
      queryClient.invalidateQueries({ queryKey: ['my-listings'] });
      queryClient.invalidateQueries({ queryKey: ['edit-property'] });
      const msg = result.approvedEdit
        ? 'Edits submitted for QC review. Your live listing remains visible until approved.'
        : result.submitForReview
          ? (isEditMode ? 'Submitted for QC review!' : 'Property submitted for review!')
          : (isEditMode ? 'Changes saved.' : 'Property saved as draft!');
      toast.success(msg);
      if (result.submitForReview || !isEditMode) navigate('/dashboard/listings');
    },
    onError: (err: any) => toast.error(err.message),
  });

  if (isAgentWithoutAgency) {
    return (
      <div className="max-w-lg mx-auto text-center py-16">
        <h2 className="font-display text-xl font-bold mb-2">Agency Membership Required</h2>
        <p className="text-muted-foreground mb-4">You must belong to an agency to post properties. Contact your agency admin for an invite link.</p>
        <Button onClick={() => navigate('/agencies')}>Browse Agencies</Button>
      </div>
    );
  }

  if (isEditMode && loadingExisting) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const currentStatus = loadedProperty?.approval_status || 'draft';
  const banner = statusBanner[currentStatus] || statusBanner.draft;
  const BannerIcon = banner.icon;
  const wasApproved = currentStatus === 'approved';
  const hasPending = !!loadedProperty?.has_pending_changes;
  const submitLabel = !isEditMode
    ? 'Submit for Review'
    : wasApproved
      ? 'Submit Edits for Review'
      : 'Submit for Review';

  // ===== Wizard state =====
  const [step, setStep] = useState(1);
  const [maxReached, setMaxReached] = useState(1);

  const validateStep = (s: number): string | null => {
    if (s === 1) {
      if (!city) return 'Please select a city';
      if (!propertyType) return 'Please select a property type';
    }
    if (s === 2) {
      if (!titleEn.trim()) return 'Title (English) is required';
      if (!price) return 'Price is required';
    }
    if (s === 5) {
      if (!contactPhone.trim()) return 'Contact phone is required';
    }
    return null;
  };

  const goNext = () => {
    const err = validateStep(step);
    if (err) { toast.error(err); return; }
    const next = Math.min(5, step + 1);
    setStep(next);
    setMaxReached(m => Math.max(m, next));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const goBack = () => { setStep(s => Math.max(1, s - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const jump = (id: number) => { if (id <= maxReached) { setStep(id); window.scrollTo({ top: 0, behavior: 'smooth' }); } };

  const handleSubmit = () => {
    // Validate all steps before final submit
    for (let s = 1; s <= 5; s++) {
      const err = validateStep(s);
      if (err) { toast.error(err); setStep(s); return; }
    }
    saveMutation.mutate(true);
  };


  return (
    <QuotaUpsell kind="listing" skip={isEditMode}>
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard/listings')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h2 className="font-display text-xl font-bold">{isEditMode ? 'Edit Property' : 'Add New Property'}</h2>
      </div>

      {/* Status banner (edit mode) */}
      {isEditMode && (
        <Card className={`border ${banner.color}`}>
          <CardContent className="py-4 flex items-start gap-3">
            <BannerIcon className="h-5 w-5 mt-0.5 shrink-0" />
            <div className="flex-1 space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={banner.color}>{banner.label}</Badge>
                {hasPending && <Badge variant="outline" className="border-blue-400 text-blue-700 bg-blue-50">Edits pending QC review</Badge>}
              </div>
              <p className="text-sm">{banner.description}</p>
              {hasPending && (
                <p className="text-xs text-muted-foreground italic">
                  You're editing pending changes. Submitting again will overwrite your previously submitted edits.
                </p>
              )}
              {loadedProperty?.reviewer_notes && (
                <div className="mt-2 p-2 rounded bg-background/60 border text-xs">
                  <strong>Latest QC feedback:</strong> {loadedProperty.reviewer_notes}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <WizardProgress steps={WIZARD_STEPS} current={step} maxReached={maxReached} onJump={jump} />

      {step === 1 && (
        <StepShell
          title="Where is your property?"
          subtitle="Tell us the purpose, type, and location."
          isFirst
          onNext={goNext}
          onSaveDraft={() => saveMutation.mutate(false)}
          saving={saveMutation.isPending}
          showDraft={!isEditMode || currentStatus === 'draft'}
        >
          <Step1Purpose
            listingType={listingType} setListingType={setListingType}
            propertyType={propertyType} setPropertyType={setPropertyType}
            city={city} setCity={setCity}
            subCity={subCity} setSubCity={setSubCity}
            woreda={woreda} setWoreda={setWoreda}
            address={address} setAddress={setAddress}
            latitude={latitude} setLatitude={setLatitude}
            longitude={longitude} setLongitude={setLongitude}
          />
        </StepShell>
      )}

      {step === 2 && (
        <StepShell
          title="Property details"
          subtitle="Add the title, price, area, and rooms."
          onBack={goBack}
          onNext={goNext}
          onSaveDraft={() => saveMutation.mutate(false)}
          saving={saveMutation.isPending}
          showDraft={!isEditMode || currentStatus === 'draft'}
        >
          <Step2Details
            titleEn={titleEn} setTitleEn={setTitleEn}
            titleAm={titleAm} setTitleAm={setTitleAm}
            descEn={descEn} setDescEn={setDescEn}
            descAm={descAm} setDescAm={setDescAm}
            price={price} setPrice={setPrice}
            currency={currency} setCurrency={setCurrency}
            areaSqm={areaSqm} setAreaSqm={setAreaSqm}
            yearBuilt={yearBuilt} setYearBuilt={setYearBuilt}
            bedrooms={bedrooms} setBedrooms={setBedrooms}
            bathrooms={bathrooms} setBathrooms={setBathrooms}
            floor={floor} setFloor={setFloor}
            totalFloors={totalFloors} setTotalFloors={setTotalFloors}
            propertyType={propertyType}
          />
        </StepShell>
      )}

      {step === 3 && (
        <StepShell
          title="Add photos"
          subtitle="Great photos can boost inquiries by up to 5x."
          onBack={goBack}
          onNext={goNext}
          onSaveDraft={() => saveMutation.mutate(false)}
          saving={saveMutation.isPending}
          showDraft={!isEditMode || currentStatus === 'draft'}
        >
          <Step3Photos
            images={images}
            uploading={uploading}
            onUpload={handleImageUpload}
            onRemove={removeImage}
            onSetPrimary={setPrimaryImage}
            onReorder={reorderImages}
          />
        </StepShell>
      )}

      {step === 4 && (
        <StepShell
          title="Features & amenities"
          subtitle="Select everything that applies — buyers filter by these."
          onBack={goBack}
          onNext={goNext}
          onSaveDraft={() => saveMutation.mutate(false)}
          saving={saveMutation.isPending}
          showDraft={!isEditMode || currentStatus === 'draft'}
        >
          <Step4Features features={features} setFeatures={setFeatures} />
        </StepShell>
      )}

      {step === 5 && (
        <StepShell
          title="Contact & review"
          subtitle="Confirm your contact info and review the listing before submitting."
          onBack={goBack}
          isFinal
          onSubmit={handleSubmit}
          submitLabel={submitLabel}
          onSaveDraft={() => saveMutation.mutate(false)}
          saving={saveMutation.isPending}
          showDraft={!isEditMode || currentStatus === 'draft' || (isEditMode && currentStatus !== 'draft' && !wasApproved)}
        >
          <Step5Review
            contactPerson={contactPerson} setContactPerson={setContactPerson}
            contactPhone={contactPhone} setContactPhone={setContactPhone}
            contactEmail={contactEmail} setContactEmail={setContactEmail}
            titleEn={titleEn}
            listingType={listingType}
            propertyType={propertyType}
            city={city} subCity={subCity}
            price={price} currency={currency}
            areaSqm={areaSqm}
            bedrooms={bedrooms} bathrooms={bathrooms}
            features={features}
            images={images}
            assignedAgentId={assignedAgentId}
            setAssignedAgentId={setAssignedAgentId}
            onJump={jump}
          />
        </StepShell>
      )}
    </div>
    </QuotaUpsell>
  );
}
